import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { useToast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { RuleForm } from '@/components/finance/RuleForm';
import { scheduleSummary, typeLabel } from './finance.rules';
import { apartmentsQuery } from '@/lib/apartments';
import { errorMessage } from '@/lib/errors';
import {
  financeAccountsQuery,
  financeCategoriesQuery,
  financeCounterpartiesQuery,
  financeExpectedByRuleQuery,
  financeRuleQuery,
  financeRulesQuery,
  financeSettingsQuery,
} from '@/lib/finance';
import { usePermissions } from '@/lib/me';
import { formatMinor } from '@/lib/money';
import { FINANCE_EXPECTED_STATUS_TONE } from '@/lib/status-tone';

export const Route = createFileRoute('/_app/finance/rules/$id')({
  component: RuleDetailRoute,
});

// The route reads the param; the page takes it as a prop (same split as the
// movement/account detail pages), so it can be mounted in a test without a
// matched route. `key={id}` forces a full remount on a param change — no
// leftover field state (or a stale operation_id, if this page ever grows
// one of its own) can survive from one rule's edit session into another's.
function RuleDetailRoute() {
  const { id } = Route.useParams();
  return <RuleDetailPage key={id} id={id} />;
}

export function RuleDetailPage({ id }: { id: string }) {
  const { can } = usePermissions();
  const { toast } = useToast();
  const canManage = can('finance.manage');

  const rule = useQuery(financeRuleQuery(id));
  const expected = useQuery(financeExpectedByRuleQuery(id));
  const { data: categories = [] } = useQuery(financeCategoriesQuery());
  const { data: apartments = [] } = useQuery(apartmentsQuery);
  const { data: counterparties = [] } = useQuery(financeCounterpartiesQuery());
  const { data: accounts = [] } = useQuery(financeAccountsQuery());
  const { data: allRules = { items: [] } } = useQuery(financeRulesQuery({ includeDisabled: true }));
  const { data: settings } = useQuery(financeSettingsQuery);

  if (!can('finance.read') || errorStatus(rule.error) === 403) return <AccessDenied />;
  if (rule.isLoading) return <p className="text-sm text-muted">Loading rule…</p>;
  if (rule.isError || !rule.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {errorMessage(rule.error)}
      </p>
    );
  }

  const r = rule.data;
  const ruleNames = Object.fromEntries(allRules.items.map((x) => [x.id, x.name]));
  const apartmentNames = Object.fromEntries(apartments.map((a) => [a.id, a.title]));
  const items = expected.data?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <Link to="/finance/rules" className="text-sm text-primary hover:underline">
        ← All rules
      </Link>

      {canManage ? (
        <RuleForm
          refs={{
            categories,
            apartments: apartments.map((a) => ({ id: a.id, title: a.title })),
            counterparties,
            accounts,
            parentCandidates: allRules.items,
            importCutoff: settings?.import_cutoff,
          }}
          initial={r}
          onDone={() => toast({ kind: 'success', title: 'Rule updated' })}
        />
      ) : (
        // A finance.read-only actor can see this page (the queue and the
        // rules table both link here) but has no write action the server
        // would honor — offering RuleForm's live Save button anyway would
        // be a control this actor can click and always watch fail.
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-4 text-sm">
          <dt className="text-muted">Type</dt>
          <dd>{typeLabel(r.type)}</dd>
          <dt className="text-muted">Name</dt>
          <dd>{r.name}</dd>
          <dt className="text-muted">Object</dt>
          <dd>{r.scope === 'company' ? 'Company' : (apartmentNames[r.apartment_id ?? ''] ?? r.apartment_id)}</dd>
          <dt className="text-muted">Amount</dt>
          <dd>{r.type === 'per_stay' && r.parent_rule_id ? '—' : formatMinor(r.amount_minor, r.currency)}</dd>
          <dt className="text-muted">Schedule</dt>
          <dd>{scheduleSummary(r, ruleNames)}</dd>
          <dt className="text-muted">Status</dt>
          <dd>
            {r.enabled ? (
              <Badge variant="success" dot>
                Enabled
              </Badge>
            ) : (
              <Badge variant="neutral" dot>
                Disabled
              </Badge>
            )}
          </dd>
        </dl>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-[0.06em] text-muted">Recent expectations</h2>
        {expected.isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted">Nothing generated from this rule yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-hairline)] text-left text-xs uppercase tracking-[0.06em] text-muted">
                  <th className="px-4 py-2.5 font-semibold">Due</th>
                  <th className="px-4 py-2.5 font-semibold">Amount</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => {
                  const tone = FINANCE_EXPECTED_STATUS_TONE[e.status];
                  return (
                    <tr key={e.id} className="border-t border-[var(--border-hairline)] hover:bg-[var(--surface-sunken)]">
                      <td className="px-4 py-3">
                        <Link to="/finance/expected/$id" params={{ id: e.id }} className="text-primary hover:underline">
                          {e.due_date}
                        </Link>
                      </td>
                      <td className="px-4 py-3 tabular-nums">{formatMinor(e.amount_minor, e.currency)}</td>
                      <td className="px-4 py-3">
                        <Badge variant={tone.variant} dot>
                          {tone.label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
